/**
 * Die Kopfzeile kennt ihren Platz — das Messen und das Anwenden. (22.09.2026,
 * aus app.ts hierher gezogen am 05.10.2026, damit es im echten Browser geprüft
 * werden kann: app.ts startet beim Laden die ganze App.)
 *
 * Die Entscheidung, WER bei Enge weicht, steht in `kopf-platz.ts` (rein, geprüft).
 */
import { gesteuert } from "./ruckel-schalter.js";
import { wasWeichenMuss } from "./kopf-platz.js";

/* -------------------------------------------------------------------------
 * Die Kopfzeile kennt ihren Platz (22.09.2026)
 *
 * Gemessen an Martins Telefon (390 Punkte, alle Anzeigen aktiv): die Leiste
 * braucht 397 Punkte. Die Glocke stand halb draussen, die Update-Pille 43
 * Punkte KOMPLETT im Nichts — der Knopf „Neue Version" war nicht antippbar,
 * waehrend die App auf einem alten Stand festhing.
 *
 * Die Entscheidung, wer bei Enge weicht, steht in `kopf-platz.ts` und ist
 * dort geprueft. Hier steht nur das Messen und das Anwenden.
 * ---------------------------------------------------------------------- */
let kopfPlatzLaeuft = false;
/** Der Wächter über die Leiste — ordneKopfzeile schaltet ihn für die Dauer der Messung ab. */
let kopfWaechter: MutationObserver | null = null;
let kopfWaechterZiel: HTMLElement | null = null;
const KOPF_WAECHTER_AUF: MutationObserverInit = {
  subtree: true,
  childList: true,
  attributes: true,
  attributeFilter: ["hidden", "style", "class"],
  characterData: true,
};

export function ordneKopfzeile(): void {
  /*
   * ⚠️ Eine eigene Klasse, NICHT `hidden`. Aufnahme, Glocke und
   *    „Neue Version" setzen ihr `hidden` selbst, je nach Inhalt — wer hier
   *    dasselbe Attribut benutzt, hat zwei Besitzer fuer einen Zustand und
   *    sie kippen sich gegenseitig. Das haeufigste Muster in diesem Haus.
   */
  const meta = document.querySelector<HTMLElement>(".topbar-meta");
  const mitte = document.querySelector<HTMLElement>(".topbar-center");
  const bar = document.querySelector<HTMLElement>(".topbar-inner") ??
    document.querySelector<HTMLElement>(".topbar");
  if (!meta || !bar || kopfPlatzLaeuft) return;
  kopfPlatzLaeuft = true;
  /*
   * 05.10.2026: Messen heißt zuerst alles zeigen, dann wieder ausblenden — das
   * sind eigene Klassenwechsel. Der Wächter sah sie und rief die Messung im
   * nächsten Bild erneut, solange irgendetwas wich (gemessen: jedes Bild, auf
   * dem Telefon dauernd). `kopfPlatzLaeuft` schützt nur INNERHALB der
   * Messung, nicht vor dem Echo danach. Darum: Wächter für die Dauer der
   * Messung ab; was er bis dahin gesammelt hat, ist unser eigenes Echo.
   */
  kopfWaechter?.disconnect();
  try {
    // Erst alles zeigen — sonst misst die Messung ihr eigenes Ergebnis.
    for (const el of bar.querySelectorAll<HTMLElement>(".kopf-weicht")) {
      el.classList.remove("kopf-weicht");
    }
    /*
     * ⚠️ Die GANZE Leiste, nicht nur der rechte Cluster. Sonst loest man
     *    den Ueberlauf rechts und erzeugt ihn links: gemessen schrumpfte
     *    die Marke „Vespera" auf „V …" statt zu weichen. Ein abgeschnittenes
     *    Wort ist schlechter als kein Wort.
     */
    const zaehlt = (k: Element): k is HTMLElement => {
      if (!(k instanceof HTMLElement)) return false;
      if (k.hasAttribute("hidden")) return false;
      if (k.id === "menu-btn") return false; // der Weg ins Menue bleibt immer
      return getComputedStyle(k).display !== "none";
    };
    const sichtbar: HTMLElement[] = [];
    for (const k of bar.children) {
      if (!zaehlt(k)) continue;
      // Die beiden Sammelbehaelter selbst zaehlen nicht — ihre Kinder schon.
      if (k === meta || k === mitte) {
        for (const kk of k.children) if (zaehlt(kk)) sichtbar.push(kk);
        continue;
      }
      sichtbar.push(k);
    }
    if (sichtbar.length < 2) return;
    /*
     * 05.10.2026: der Platz wird genau gerechnet — Innenrand der Leiste und
     * der Abstand zum Menü-Knopf gehen ab. Vorher stand hier „2 × Abstand“ als
     * Schätzung; mit den neuen Abständen (8 statt 14 Punkte) hätte sie zu viel
     * Platz versprochen.
     */
    const barStil = getComputedStyle(bar);
    const innenRand =
      (parseFloat(barStil.paddingLeft) || 0) + (parseFloat(barStil.paddingRight) || 0);
    const abstand = Math.max(
      ...[bar, meta, mitte].map((e) =>
        e ? parseFloat(getComputedStyle(e).columnGap) || 0 : 0,
      ),
      1,
    );
    let linksBelegt = 0;
    const menue = document.getElementById("menu-btn");
    if (menue && menue.getBoundingClientRect().width > 0) {
      linksBelegt += menue.getBoundingClientRect().width + (parseFloat(barStil.columnGap) || 0);
    }
    const budget = Math.max(0, bar.clientWidth - innenRand - linksBelegt);
    /*
     * ⚠️ Wer sich selbst kürzen kann (flex-shrink > 0 und eine Mindestbreite),
     *    zählt mit dieser Mindestbreite — seine gemessene ist schon die gekürzte.
     *    (Bis 11.10.2026 war das das Projekt; heute kürzt sich in der Leiste niemand mehr.)
     */
    const kleinsteBreite = (k: HTMLElement, breite: number): number => {
      const st = getComputedStyle(k);
      if (parseFloat(st.flexShrink) === 0) return breite;
      const m = parseFloat(st.minWidth);
      return Number.isFinite(m) ? Math.min(m, breite) : breite;
    };
    const posten = sichtbar.map((k) => {
      const breite = k.getBoundingClientRect().width;
      return {
        id: k.id || k.className.split(" ")[0] || "",
        breite,
        min: kleinsteBreite(k, breite),
        fest: k.dataset.kopfFest === "1",
      };
    });
    const weg = new Set(wasWeichenMuss(posten, budget, abstand));
    for (const k of sichtbar) {
      const id = k.id || k.className.split(" ")[0] || "";
      if (weg.has(id)) k.classList.add("kopf-weicht");
    }
    /*
     * ⚠️ Marke und Bot-Name tragen eigene IDs nicht am aeusseren Knopf.
     *    Der Knopf `brand-home` umschliesst `.brand-text` — gemeint ist
     *    immer das Ganze.
     */
    if (weg.has("brand-home")) {
      document.getElementById("brand-home")?.classList.add("kopf-weicht");
    }
  } finally {
    kopfPlatzLaeuft = false;
    if (kopfWaechter && kopfWaechterZiel) kopfWaechter.observe(kopfWaechterZiel, KOPF_WAECHTER_AUF);
  }
}

/*
 * Neu ordnen, wenn sich die Leiste aendert oder das Fenster dreht.
 * 05.10.2026: der Wächter steht auf der GANZEN Leiste (.topbar-inner), nicht
 * mehr nur auf der rechten Gruppe — das aktive Projekt steht seit heute links
 * beim Namen, und sein Name oder Erscheinen ändert den Platz genauso.
 */
/** Den Wächter und das Neu-Ordnen bei Drehung starten (einmal, beim Laden der App). */
export function starteKopfOrdnung(): void {
  const bar =
    document.querySelector<HTMLElement>(".topbar-inner") ?? document.querySelector<HTMLElement>(".topbar");
  if (!bar) return;
  const plane = (): void => {
    requestAnimationFrame(() => ordneKopfzeile());
  };
  kopfWaechterZiel = bar;
  kopfWaechter = new MutationObserver(gesteuert("wachen", plane, true));
  kopfWaechter.observe(bar, KOPF_WAECHTER_AUF);
  window.addEventListener("resize", plane);
  plane();
}

